import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import { getFormValidationTranslations } from '@common/i18n/formValidation';
import type { TemplateFormField } from '@common/types/templateFormFields';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { hasDuplicateContactEmails } from '@common/utils/contactEmails';
import { isValidEmail } from '@common/utils/isValidEmail';
import { DateTime } from 'luxon';

function isValidDate(date: Date): boolean {
    return !Number.isNaN(date.getTime());
}

function toDate(value: unknown): Date | null {
    if (DateTime.isDateTime(value)) {
        const date = value.toJSDate();
        return isValidDate(date) ? date : null;
    }
    if (value instanceof Date) {
        return isValidDate(value) ? value : null;
    }
    if (typeof value === 'string' || typeof value === 'number') {
        const date = new Date(value);
        return isValidDate(date) ? date : null;
    }
    return null;
}

/**
 * Field-level validation used by public intake/quote forms.
 * Kept free of Zod so the client form bundle does not pull that runtime.
 */
export function validate(
    type: string,
    value: string | number | Date | DateTime,
    extraParam: string | number | null = null,
    language: TemplateFormLanguage = DEFAULT_TEMPLATE_FORM_LANGUAGE,
): true | string {
    const t = getFormValidationTranslations(language);

    switch (type) {
        case 'email': {
            if (!value) return true;
            if (typeof value !== 'string' || value.trim() === '') {
                return t.emailRequired;
            }
            return isValidEmail(value) ? true : t.invalidEmail;
        }
        case 'number': {
            // Match prior Zod behavior: treat falsy values as "not provided".
            if (!value) return true;
            if (typeof value !== 'number' || Number.isNaN(value)) {
                return t.numberRequired;
            }
            return true;
        }
        case 'string': {
            if (!value) return true;
            if (typeof value !== 'string' || value.length < 1) {
                return t.fieldCannotBeEmpty;
            }
            return true;
        }
        case 'date': {
            const date = toDate(value);
            if (!date) {
                return value == null || value === '' ? t.dateRequired : t.invalidDate;
            }
            return true;
        }
        case 'pattern':
            if (extraParam && typeof extraParam === 'string') {
                if (typeof value !== 'string' || value.length < 1) {
                    return t.fieldCannotBeEmpty;
                }
                try {
                    if (!new RegExp(extraParam).test(value)) {
                        return t.invalidValue;
                    }
                } catch {
                    return 'Extra parameter must be a valid regex pattern for type "pattern"';
                }
                return true;
            }
            return 'Extra parameter must be a valid regex pattern for type "pattern"';
        case 'max':
            if (extraParam && typeof extraParam === 'number') {
                const text = value == null ? '' : String(value);
                if (text.length > extraParam) {
                    return t.maximumLength(extraParam);
                }
                return true;
            }
            return 'Extra parameter must be a number for type "max"';
        case 'min':
            if (extraParam && typeof extraParam === 'number') {
                const text = value == null ? '' : String(value);
                if (text.length < extraParam) {
                    return t.minimumLength(extraParam);
                }
                return true;
            }
            return 'Extra parameter must be a number for type "min"';
        case 'fileSize':
        case 'fileType':
        case 'fileCount':
        case 'notEqual':
            return true;
        case 'required':
            if (value !== null && value !== undefined && value !== '') {
                return true;
            }
            return t.fieldRequired;
        default:
            return `Validation type '${type}' is not supported.`;
    }
}

function getTrimmedFieldValue(field: TemplateFormField[] | undefined, fieldName: string): string {
    const value = field?.find((item) => item.name === fieldName)?.questionSchema?.value;
    return value?.toString().trim() ?? '';
}

export function isFormValidForDraftSubmission(sellers: any[], wrapCount: number) {
    if (wrapCount === 0) return false;

    const activeSellers = sellers.slice(0, wrapCount);
    const hasValidContacts = activeSellers.every((seller) => {
        const hasRequiredFields = ['firstName', 'lastName'].every((field) =>
            getTrimmedFieldValue(seller, field),
        );
        if (!hasRequiredFields) return false;

        const email = getTrimmedFieldValue(seller, 'email');
        return isValidEmail(email);
    });

    if (!hasValidContacts) return false;

    const emails = activeSellers.map((seller) => getTrimmedFieldValue(seller, 'email'));
    return !hasDuplicateContactEmails(emails);
}
